import { Button } from '@/components/ui/button';
import { Link } from 'wouter';
import { difficultyNames } from '@/components/game-attributes/Difficulty';
import { formatHashtag } from '@/lib/text.utils';
import { cn } from '@/lib/utils';
import Play2 from '@icons/play2.svg?react';
import { useToast } from '@/hooks/use-toast';
import { LoaderCircle, Maximize2, Minimize2, Volume2, VolumeOff } from 'lucide-react';
import { FocusTrap } from 'focus-trap-react';
import CrossFilled from '@icons/cross-filled.svg?react';
import ymWrapper from '@/lib/ym';
import styles from './game-hero.module.css';
import { HomeGame } from '@/types/game';
import { Category } from '@/types/category';
import { useRef, useState } from 'react';
import { useLockBodyScroll } from 'react-use';

interface GameHeroProps {
  priceText: React.ReactNode;
  game: HomeGame;
  isGamePurchased: boolean;
  isLaunching: boolean;
  categories: Category[];
  handleBuy: () => void;
  handlePromocodeClick: () => void;
  handleGameLaunch: () => void;
}

export function GameHero({
  priceText,
  game,
  isLaunching,
  isGamePurchased,
  categories,
  handleBuy,
  handlePromocodeClick,
  handleGameLaunch,
}: GameHeroProps) {
  const { toast } = useToast();
  const trailerRef = useRef<HTMLVideoElement>(null);

  const [isTrailerFullscreen, setIsTrailerFullscreen] = useState(false);
  const [isTrailerMuted, setIsTrailerMuted] = useState(true);

  useLockBodyScroll(isTrailerFullscreen);

  return (
    <div className="relative pb-6 md:py-20 md:mb-12 -mt-20 pt-30">
      <FocusTrap active={isTrailerFullscreen}>
        <div
          className={cn(
            styles.trailer,
            'relative lg:absolute lg:right-0 aspect-video w-full lg:w-[70vw] max-h-full',
          )}
        >
          {(game.trailerUrl || game.backgroundUrl) && (
            <>
              {isTrailerFullscreen && (
                <button
                  onClick={() => setIsTrailerFullscreen(false)}
                  className="size-12 fixed left-3 top-2 flex items-center justify-center z-[2500]"
                >
                  <CrossFilled className="fill-white/10 hover:fill-white transition-colors duration-300" />
                </button>
              )}
              <TrailerControls
                isMuted={isTrailerMuted}
                toggleMute={() => setIsTrailerMuted((v) => !v)}
                isFullscreen={isTrailerFullscreen}
                toggleFullscreen={() => setIsTrailerFullscreen((v) => !v)}
                containerClassName={cn(
                  'font-bold text-lg md:text-xl h-12',
                  'flex justify-end mr-3 mb-2 absolute right-0 bottom-0 z-[3000]',
                  isTrailerFullscreen ? 'fixed' : 'bottom-[25%] lg:hidden',
                )}
              />
              <video
                src={game.trailerUrl + '#t=0.1'}
                className={cn(
                  'w-full h-full object-cover',
                  isTrailerFullscreen && 'fixed inset-0 z-[2000] object-contain bg-background',
                )}
                ref={trailerRef}
                playsInline
                preload="metadata"
                autoPlay
                loop
                onClick={() => {
                  if (!trailerRef.current) return;
                  if (trailerRef.current.paused) {
                    trailerRef.current.play();
                    ymWrapper('reachGoal', 'play_trailer_clicked');
                  } else {
                    trailerRef.current.pause();
                    ymWrapper('reachGoal', 'pause_trailer_clicked');
                  }
                }}
                muted={isTrailerMuted}
                poster={game.backgroundUrl ?? undefined}
                onError={(e) => {
                  console.error('Trailer error:', e);
                  toast({
                    variant: 'destructive',
                    title: 'Ошибка воспроизведения',
                    description: 'Не удалось загрузить трейлер',
                  });
                }}
              />
            </>
          )}
        </div>
      </FocusTrap>
      <div className="z-30 relative main-container">
        <div className="lg:mt-32">
          {game?.heroImageUrl ? (
            <img
              src={game.heroImageUrl}
              alt=""
              className="mb-4 relative max-w-[300px] sm:max-w-[510px] lg:max-w-[660px]"
            />
          ) : (
            <h1 className="text-6xl lg:text-7xl font-black mb-4 relative max-w-80 sm:max-w-2xl">
              <span
                className="!text-background inset-0 absolute z-10"
                style={{
                  textShadow: `
                0 0 6px rgba(255, 255, 255, 1),
                0 0 9px rgba(255, 255, 255, 0.8),
                0 0 12px rgba(255, 255, 255, 0.5),
                0 0 15px rgba(210, 180, 255, 0.4)`,
                }}
              >
                {game.title}
              </span>
              <span
                className="inset-0 absolute text-transparent z-20"
                style={{
                  WebkitTextStroke: '3px white',
                  textShadow: `
                0 0 20px rgba(255, 255, 255, 0.3)
              `,
                }}
              >
                {game.title}
              </span>
              {game.title}
            </h1>
          )}
          <div className="mb-6 flex flex-wrap gap-3 text-base sm:text-xl sm:leading-10 2xl:text-2xl 2xl:leading-10 font-medium text-muted-foreground">
            {game.difficulty && <span>{difficultyNames[game.difficulty]} </span>}
            <div className="flex items-center gap-2">
              <span>{game.duration} минут</span>
            </div>
            <div className="flex items-center gap-2">
              <span>{game.rounds} раундов</span>
            </div>
            <div className="flex items-center gap-2">
              <span>{game.questions} вопросов</span>
            </div>
            {/* <div className="flex items-center gap-2"> */}
            {/*   <span>{game.initialViews} просмотров</span> */}
            {/* </div> */}
          </div>

          {game.description && (
            <section className="mb-2">
              <p
                className="max-w-screen-md md:text-xl xl:text-2xl"
                dangerouslySetInnerHTML={{ __html: game.description }}
              />
            </section>
          )}

          {/* Tags Section */}
          <div className="flex flex-wrap gap-4 mb-20 text-purple-300 text-base sm:text-xl sm:leading-10 2xl:text-2xl 2xl:leading-10">
            {game.isOnNew && <span>#новое</span>}
            {game.isRecommended && <span>#рекомендуем</span>}
            {game.categoryIds &&
              game.categoryIds.length > 0 &&
              game.categoryIds.map((categoryId) => {
                const category = categories?.find((c) => c.id === categoryId);
                if (!category) return null;
                return (
                  <Link key={category.id} to={`/games?categoryId=${category.id}`}>
                    <span>#{formatHashtag(category.name)}</span>
                  </Link>
                );
              })}
          </div>

          {!game.isFree && (
            <div className="font-bold text-primary text-3xl hidden sm:block mb-8">{priceText}</div>
          )}

          <div className="flex gap-4 items-center mb-6 flex-col sm:flex-row sm:text-2xl font-bold">
            {!game.isFree && (
              <>
                <Button
                  onClick={!isGamePurchased ? handleBuy : handleGameLaunch}
                  className="w-full lg:px-10 lg:max-w-min"
                  disabled={isLaunching}
                  data-goal-id={!isGamePurchased ? 'buy_game_clicked' : 'play_game_clicked'}
                >
                  {isLaunching ? (
                    <LoaderCircle className="stroke-current h-7 animate-spin" />
                  ) : (
                    <Play2 className="fill-current h-7" />
                  )}
                  {!isGamePurchased ? 'Купить и играть' : 'Играть'}
                </Button>
                {!isGamePurchased && (
                  <Button
                    variant="ghost"
                    className="ml-0 w-full lg:px-8 lg:max-w-min"
                    onClick={() => handlePromocodeClick()}
                    data-goal-id="enter_promocode_clicked"
                  >
                    Ввести промокод
                  </Button>
                )}
              </>
            )}

            {game.isFree && (
              <>
                <Button
                  className="w-full sm:w-auto"
                  onClick={handleGameLaunch}
                  disabled={isLaunching}
                  data-goal-id="play_free_game_clicked"
                >
                  {isLaunching ? (
                    <LoaderCircle className="stroke-current h-7 animate-spin" />
                  ) : (
                    <Play2 className="fill-current h-7" />
                  )}
                  Играть
                </Button>
                <span className="font-bold text-primary">Бесплатно</span>
              </>
            )}

            {game.trailerUrl && (
              <TrailerControls
                isMuted={isTrailerMuted}
                toggleMute={() => setIsTrailerMuted((v) => !v)}
                isFullscreen={isTrailerFullscreen}
                toggleFullscreen={() => setIsTrailerFullscreen((v) => !v)}
                containerClassName={cn(
                  'font-bold text-xl h-12',
                  'ml-auto justify-end hidden lg:flex',
                  isTrailerFullscreen ? 'fixed' : 'bottom-[25%]',
                )}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function TrailerControls({
  isMuted,
  toggleMute,
  isFullscreen,
  toggleFullscreen,
  containerClassName,
}: {
  isMuted: boolean;
  toggleMute: () => void;
  isFullscreen: boolean;
  toggleFullscreen: () => void;
  containerClassName: string;
}) {
  return (
    <div className={containerClassName}>
      <Button
        className={cn('aspect-square bg-black/50 hover:bg-black/70 text-purple-100 rounded-md')}
        onClick={toggleMute}
        data-goal-id={isMuted ? 'unmute_trailer_clicked' : 'mute_trailer_clicked'}
      >
        {isMuted ? (
          <VolumeOff className="stroke-[2.5] size-6" />
        ) : (
          <Volume2 className="stroke-[2.5] size-6" />
        )}
      </Button>
      <Button
        className={cn(
          'bg-black/50 hover:bg-black/70 text-purple-100 rounded-md p-[16px]',
          isFullscreen && 'aspect-square',
        )}
        onClick={toggleFullscreen}
        data-goal-id={!isFullscreen ? 'expand_trailer_clicked' : 'minimize_trailer_clicked'}
      >
        {!isFullscreen ? (
          <p className="flex items-center gap-3">
            <Maximize2 className="inline-block self-center stroke-[3] size-6" /> Трейлер
          </p>
        ) : (
          <Minimize2 className="stroke-[3] size-6" />
        )}
      </Button>
    </div>
  );
}
